import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Megaphone } from 'lucide-react';
import { InfluencerDashboard } from '@/components/InfluencerDashboard';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Field = ({ id, label, children }) => (
  <div>
    <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function InfluencerProposal() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState('loading');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', socials: '', followers: '', nif: '', iban: '', address: '', amount_cve: '', deliverables: '', description: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    axios.get(`${API_URL}/api/influencers/invite/${token}`)
      .then((r) => { setInvite(r.data); setForm((f) => ({ ...f, name: r.data.name || '', email: r.data.email || '' })); setState('ok'); })
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/influencers/invite/${token}/proposal`, {
        ...form,
        followers: form.followers ? parseInt(form.followers, 10) : null,
        amount_cve: parseInt(form.amount_cve, 10),
        phone: form.phone || null, nif: form.nif || null, iban: form.iban || null, address: form.address || null, description: form.description || null,
      });
      const { data } = await axios.get(`${API_URL}/api/influencers/invite/${token}`);
      setInvite(data);
      setState('submitted');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : t('influencer.errorSubmit'));
    } finally {
      setSaving(false);
    }
  };

  const wrap = (inner) => (
    <main className="min-h-screen bg-slate-50 py-10 px-4">
      <Helmet><title>{t('influencer.pageTitle')}</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="max-w-2xl mx-auto">{inner}</div>
    </main>
  );

  if (state === 'loading') return wrap(<div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>);
  if (state === 'invalid') return wrap(<Card className="p-8 text-center text-slate-600" data-testid="influencer-invalid">{t('influencer.invalid')}</Card>);

  const locked = state === 'submitted' || !['invited', 'rejected'].includes(invite.status);
  if (locked) {
    return wrap(<InfluencerDashboard invite={{ ...invite, status: state === 'submitted' ? 'proposal_submitted' : invite.status }} />);
  }

  return wrap(
    <>
      <header className="mb-8">
        <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3 flex items-center gap-2"><Megaphone className="h-4 w-4" />{t('influencer.eyebrow')}</p>
        <h1 className="text-[28px] sm:text-[36px] font-bold tracking-tighter text-slate-900 leading-[1.1]">{t('influencer.title', { name: invite.name })}</h1>
        <p className="mt-3 text-[15px] text-slate-600">{t('influencer.subtitle')}</p>
        {invite.status === 'rejected' && <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">{t('influencer.resubmit')}</p>}
      </header>
      <form onSubmit={submit} className="space-y-6" data-testid="influencer-proposal-form">
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-slate-900">{t('influencer.sectionYou')}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="name" label={t('influencer.name')}><Input id="name" required value={form.name} onChange={set('name')} data-testid="inf-name" /></Field>
            <Field id="email" label={t('influencer.email')}><Input id="email" type="email" required value={form.email} onChange={set('email')} data-testid="inf-email" /></Field>
            <Field id="phone" label={t('influencer.phone')}><Input id="phone" value={form.phone} onChange={set('phone')} data-testid="inf-phone" /></Field>
            <Field id="followers" label={t('influencer.followers')}><Input id="followers" type="number" min="0" value={form.followers} onChange={set('followers')} data-testid="inf-followers" /></Field>
          </div>
          <Field id="socials" label={t('influencer.socials')}><Input id="socials" required placeholder="@instagram, TikTok, YouTube…" value={form.socials} onChange={set('socials')} data-testid="inf-socials" /></Field>
        </Card>
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-slate-900">{t('influencer.sectionProposal')}</h2>
          <Field id="amount" label={t('influencer.amount')}><Input id="amount" type="number" min="500" required value={form.amount_cve} onChange={set('amount_cve')} data-testid="inf-amount" /></Field>
          <Field id="deliverables" label={t('influencer.deliverables')}><Input id="deliverables" required placeholder={t('influencer.deliverablesPlaceholder')} value={form.deliverables} onChange={set('deliverables')} data-testid="inf-deliverables" /></Field>
          <Field id="description" label={t('influencer.description')}><Textarea id="description" rows={4} value={form.description} onChange={set('description')} data-testid="inf-description" /></Field>
        </Card>
        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-slate-900">{t('influencer.sectionBilling')}</h2>
          <p className="text-sm text-slate-500">{t('influencer.billingHint')}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field id="nif" label="NIF"><Input id="nif" value={form.nif} onChange={set('nif')} data-testid="inf-nif" /></Field>
            <Field id="iban" label="IBAN"><Input id="iban" value={form.iban} onChange={set('iban')} data-testid="inf-iban" /></Field>
          </div>
          <Field id="address" label={t('influencer.address')}><Input id="address" value={form.address} onChange={set('address')} data-testid="inf-address" /></Field>
        </Card>
        <Button type="submit" size="lg" disabled={saving} className="w-full" data-testid="inf-submit">{saving ? t('influencer.sending') : t('influencer.submit')}</Button>
      </form>
    </>
  );
}
